import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiSend, FiImage, FiMic, FiPlus, FiPaperclip, FiEdit, FiCheck, FiX } from "react-icons/fi";
import { MessageCircle, Bot, User, ThumbsUp, ThumbsDown, Copy } from "lucide-react";
import Breadcrumb from "../../components/Common/Bredcrump";
import chatBotIcon from "../../assets/chat_bot.png";
import { createMessage, listChats, generateAIMessage, updateMessageFeedback, updateChatTitle } from "../../utils/chat";
import { manageAgentSessionIds } from "../../helpers";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./ChatBot.css";

// Feedback constants
const FEEDBACK_ENUM = {
  NA: 0,
  LIKE: 1,
  DISLIKE: 2,
  COPIED: 3
};

const FEEDBACK_LABELS = {
  [FEEDBACK_ENUM.NA]: 'na',
  [FEEDBACK_ENUM.LIKE]: 'like',
  [FEEDBACK_ENUM.DISLIKE]: 'dislike',
  [FEEDBACK_ENUM.COPIED]: 'copied'
};

// Message formatter function
const formatMessage = (text) => {
  if (!text) return text;
  
  // Split by lines to handle lists
  const lines = text.split('\n');
  const formattedLines = lines.map((line, index) => {
    let formattedLine = line;
    
    // Handle bold text **text**
    formattedLine = formattedLine.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Handle code blocks `code` - improved to handle multi-line and preserve formatting
    formattedLine = formattedLine.replace(/`([^`\n]+)`/g, '<code>$1</code>');
    
    // Handle numbered lists (1., 2., etc.) - keep the numbers as text
    if (/^\d+\.\s/.test(formattedLine.trim())) {
      return `<div class="numbered-item">${formattedLine}</div>`;
    }
    
    // Handle bullet points (-, *, •)
    if (/^[-*•]\s/.test(formattedLine.trim())) {
      return `<li>${formattedLine.replace(/^[-*•]\s/, '')}</li>`;
    }
    
    return formattedLine;
  });
  
  // Check if we have any list items
  const hasListItems = formattedLines.some(line => line.startsWith('<li>') || line.startsWith('<div class="numbered-item">'));
  
  if (hasListItems) {
    let result = '';
    let inList = false;
    let isNumberedList = false;
    
    formattedLines.forEach((line, index) => {
      if (line.startsWith('<li>') || line.startsWith('<div class="numbered-item">')) {
        // Check if this is a numbered list item
        const isNumbered = line.startsWith('<div class="numbered-item">');
        
        if (!inList) {
          // Start new list
          isNumberedList = isNumbered;
          result += isNumbered ? '<div class="numbered-list">' : '<ul>';
          inList = true;
        } else if (isNumberedList !== isNumbered) {
          // Different list type, close current and start new
          result += isNumberedList ? '</div>' : '</ul>';
          isNumberedList = isNumbered;
          result += isNumbered ? '<div class="numbered-list">' : '<ul>';
        }
        
        result += line;
      } else {
        if (inList) {
          result += isNumberedList ? '</div>' : '</ul>';
          inList = false;
          isNumberedList = false;
        }
        result += line + (index < formattedLines.length - 1 ? '<br/>' : '');
      }
    });
    
    if (inList) {
      result += isNumberedList ? '</div>' : '</ul>';
    }
    
    return result;
  }
  
  return formattedLines.join('<br/>');
};

const ChatBot = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [chatName, setChatName] = useState("");
  const [chatDescription, setChatDescription] = useState("");
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isAITyping, setIsAITyping] = useState(false);
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const [showRetryButton, setShowRetryButton] = useState(false);
  const [lastFailedMessage, setLastFailedMessage] = useState("");
  // Get data from navigation state
  const { 
    tags, 
    documents, 
    uid, 
    agentId: propAgentId, 
    sessionId: propSessionId, 
    chatId, 
    chatTitle,
    uploadedFiles: initialFiles 
  } = location.state || {};
  
  // Session management - use props if available, otherwise get from session management
  const { agentId: managedAgentId, sessionId: managedSessionId } = manageAgentSessionIds();
  const agentId = propAgentId || managedAgentId;
  const sessionId = propSessionId || managedSessionId;
  
  const [uploadedFiles, setUploadedFiles] = useState(initialFiles || []);
  const [chatData, setChatData] = useState(null);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isAddingNewMessage, setIsAddingNewMessage] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [temporaryCopiedMessages, setTemporaryCopiedMessages] = useState(new Set());
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadMoreMessages = useCallback(async () => {
    if (isLoadingMore || !hasMoreMessages) return;
    
    setIsLoadingMore(true);
    
    // Store current scroll position and height
    const messagesContainer = messagesContainerRef.current;
    const previousScrollHeight = messagesContainer.scrollHeight;
    const previousScrollTop = messagesContainer.scrollTop;
    
    try {
      const nextPage = currentPage + 1;
      const response = await listChats(uid , nextPage, 10);
      
      if (response.chat && response.messages && Array.isArray(response.messages)) {
        const newMessages = response.messages
          .map((msg) => ({
          id: msg.id,
          type: msg.role === 'user' ? 'user' : 'ai',
          content: msg.content || '',
          timestamp: new Date(msg.created_at || msg.timestamp || Date.now()),
          documentUrl: msg.document_url,
          feedback: msg.feedback || 'na'
          }))
          .sort((a, b) => a.id - b.id);
        
        setMessages(prev => [...newMessages, ...prev]);
        setCurrentPage(nextPage);
        
        if (response.pagination && nextPage >= response.pagination.total_pages) {
          setHasMoreMessages(false);
        }
        
        // Restore scroll position after new messages are added
        setTimeout(() => {
          if (messagesContainer) {
            const newScrollHeight = messagesContainer.scrollHeight;
            const heightDifference = newScrollHeight - previousScrollHeight;
            messagesContainer.scrollTop = previousScrollTop + heightDifference;
          }
        }, 0);
      }
    } catch (error) {
      console.error('Error loading more messages:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMoreMessages, currentPage, uid]);

  // Only scroll to bottom for new messages, not when loading more
  useEffect(() => {
    // Only scroll to bottom if we're adding new messages, not loading more
    if (isAddingNewMessage && !isLoadingMore) {
      scrollToBottom();
      setIsAddingNewMessage(false);
    }
  }, [messages, isLoadingMore, isAddingNewMessage]);

  // Handle scroll to load more messages
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      
      if (scrollTop <= 50 && hasMoreMessages && !isLoadingMore) {
        loadMoreMessages();
      }
    };

    messagesContainer.addEventListener('scroll', handleScroll);
    return () => messagesContainer.removeEventListener('scroll', handleScroll);
  }, [hasMoreMessages, isLoadingMore, loadMoreMessages]);

  const loadChatMessages = async () => {
    setIsLoadingChat(true);
    try {
      const currentUid = uid ;
      const response = await listChats(currentUid, 1, 10);
      
      if (response.chat) {
        setChatData(response.chat);
        
        if (response.messages && Array.isArray(response.messages)) {
          const formattedMessages = response.messages
            .map((msg) => ({
              id: msg.id,
              type: msg.role === 'user' ? 'user' : 'ai',
              content: msg.content || '',
              timestamp: new Date(msg.created_at || msg.timestamp || Date.now()),
              documentUrl: msg.document_url,
              feedback: msg.feedback || 'na'
            }))
            .sort((a, b) => a.id - b.id);
          
          setMessages(formattedMessages);
          if (formattedMessages.length > 0) {
            setHasStartedChat(true);
            // Scroll to bottom when initially loading chat
            setTimeout(() => {
              scrollToBottom();
            }, 100);
          }
        }
        
        if (response.pagination) {
          setCurrentPage(response.pagination.current_page);
          setHasMoreMessages(response.pagination.current_page < response.pagination.total_pages);
        }
      }
    } catch (error) {
      console.error('Error loading chat messages:', error);
    } finally {
      setIsLoadingChat(false);
    }
  };
  useEffect(() => {
    loadChatMessages();
  }, [uid]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    // Start the chat on first message
    if (!hasStartedChat) {
      setHasStartedChat(true);
    }

    const currentMessage = inputMessage;
    setInputMessage("");
    setIsTyping(true);
    setIsAITyping(true);
    setShowRetryButton(false); // Hide retry button when sending new message

    try {
      const formData = new FormData();
      formData.append('request_type', 'free_chat');
      formData.append('additional_data[agent_id]', agentId);
      formData.append('additional_data[session_id]', sessionId);
      formData.append('additional_data[uid]', uid || managedAgentId);
      formData.append('additional_data[further_details]', currentMessage);
      
      if (uploadedFiles.length > 0) {
        formData.append('additional_data[document]', uploadedFiles[0]);
      }
      let createMessageSuccess = false;
      let createMessageResponse;
      
      try {
        createMessageResponse = await createMessage(formData);
        createMessageSuccess = createMessageResponse.success;
      } catch (createMessageError) {
        console.error('createMessage failed:', createMessageError);
        createMessageSuccess = false;
        createMessageResponse = { success: false, error: createMessageError };
      }
      
      if (createMessageSuccess) {
        const userMessage = {
          id: Date.now(),
          type: "user",
          content: currentMessage,
          timestamp: new Date(),
          feedback: 'na'
        };
        setIsAddingNewMessage(true);
        setMessages(prev => [...prev, userMessage]);
        let generateAISuccess = false;
        let retryCount = 0;
        const maxRetries = 2;
        
        while (!generateAISuccess && retryCount <= maxRetries) {
          try {
            const generateResponse = await generateAIMessage({
              request_type: "free_chat",
              additional_data: {
                agent_id: agentId,
                session_id: sessionId,
                uid: uid ,
                streaming: true,
                image_file: uploadedFiles.length > 0 ? uploadedFiles[0].name : "",
                further_details: currentMessage
              }
            });
            
            if (generateResponse) {
              generateAISuccess = true;
              
              let aiMessageContent = "";
              
              try {
                
                if (typeof generateResponse === 'string') {
                  let cleanedResponse = generateResponse;
                  if (cleanedResponse.startsWith('data: ')) {
                    cleanedResponse = cleanedResponse.substring(6);
                  }
                  
                  try {
                    const parsedResponse = JSON.parse(cleanedResponse);
                    
                    if (parsedResponse && parsedResponse.value) {
                      aiMessageContent = parsedResponse.value;
                    } else if (parsedResponse && parsedResponse.data && parsedResponse.data.value) {
                      aiMessageContent = parsedResponse.data.value;
                    } else if (parsedResponse && parsedResponse.data && parsedResponse.data.data && parsedResponse.data.data.value) {
                      aiMessageContent = parsedResponse.data.data.value;
                    }
                  } catch (jsonParseError) {
                    console.error('JSON parse error:', jsonParseError);
                    
                    const valueMatch = cleanedResponse.match(/"value":\s*"([^"]*)"\s*}/);
                    if (valueMatch && valueMatch[1]) {
                      aiMessageContent = valueMatch[1];
                    } else {
                      const altValueMatch = cleanedResponse.match(/"value":\s*"([^"]*)"\s*[,\}]/);
                      if (altValueMatch && altValueMatch[1]) {
                        aiMessageContent = altValueMatch[1];
                      }
                    }
                  }
                }
                else if (typeof generateResponse.data === 'string') {
                  let cleanedData = generateResponse.data;
                  if (cleanedData.startsWith('data: ')) {
                    cleanedData = cleanedData.substring(6);
                  }
                  
                  const parsedData = JSON.parse(cleanedData);
                  
                  if (parsedData && parsedData.value) {
                    aiMessageContent = parsedData.value;
                  } else if (parsedData && parsedData.data && parsedData.data.value) {
                    aiMessageContent = parsedData.data.value;
                  }
                } 
                else if (generateResponse.data && typeof generateResponse.data === 'object') {
                  if (generateResponse.data.value) {
                    aiMessageContent = generateResponse.data.value;
                  } else if (generateResponse.data.data && generateResponse.data.data.value) {
                    aiMessageContent = generateResponse.data.data.value;
                  }
                }
                else if (generateResponse.message) {
                  aiMessageContent = generateResponse.message;
                }
              } catch (parseError) {
                console.error('Error parsing response:', parseError);
                
                const rawResponseString = typeof generateResponse === 'string' ? generateResponse : JSON.stringify(generateResponse);
                
                const valueMatch = rawResponseString.match(/"value":\s*"([^"]*)"\s*}/);
                if (valueMatch && valueMatch[1]) {
                  aiMessageContent = valueMatch[1];
                } else {
                  const altValueMatch = rawResponseString.match(/"value":\s*"([^"]*)"\s*[,\}]/);
                  if (altValueMatch && altValueMatch[1]) {
                    aiMessageContent = altValueMatch[1];
                  }
                }
              }
              
              const aiMessage = {
                id: Date.now() + 1,
                type: "ai",
                content: aiMessageContent,
                timestamp: new Date(),
                feedback: 'na'
              };
              setIsAddingNewMessage(true);
              setMessages(prev => [...prev, aiMessage]);
            } else {
              throw new Error('No response from generateAIMessage');
            }
          } catch (generateError) {
            retryCount++;
            console.error(`generateAIMessage failed (attempt ${retryCount}):`, generateError);
            
            if (retryCount > maxRetries) {
              setShowRetryButton(true);
              setLastFailedMessage(currentMessage);
              
              const retryMessage = {
                id: Date.now() + 1,
                type: "ai",
                content: "Sorry, I'm having trouble responding right now. Please try again.",
                timestamp: new Date(),
                feedback: 'na'
              };
              setIsAddingNewMessage(true);
              setMessages(prev => [...prev, retryMessage]);
            } else {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          type: "ai",
          content: "Sorry, I couldn't save your message. Please try again.",
          timestamp: new Date(),
          feedback: 'na'
        };
        setIsAddingNewMessage(true);
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: "ai",
        content: "Sorry, I encountered an error processing your message. Please try again.",
        timestamp: new Date(),
        feedback: 'na'
      };
      setIsAddingNewMessage(true);
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
      setIsAITyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleRetry = async () => {
    if (!lastFailedMessage) return;
    
    setShowRetryButton(false);
    setIsAITyping(true);
    
    try {
      const generateResponse = await generateAIMessage({
        request_type: "free_chat",
        additional_data: {
          agent_id: agentId,
          session_id: sessionId,
          uid: uid || managedAgentId,
          streaming: true,
          image_file: uploadedFiles.length > 0 ? uploadedFiles[0].name : "",
          further_details: lastFailedMessage
        }
      });
      
      if (generateResponse) {
        let aiMessageContent = "I received your message and processed it successfully.";
        
        try {
          if (typeof generateResponse === 'string') {
            let cleanedResponse = generateResponse;
            if (cleanedResponse.startsWith('data: ')) {
              cleanedResponse = cleanedResponse.substring(6);
            }
            
            const parsedResponse = JSON.parse(cleanedResponse);
            if (parsedResponse && parsedResponse.value) {
              aiMessageContent = parsedResponse.value;
            }
          } else if (generateResponse.data && generateResponse.data.value) {
            aiMessageContent = generateResponse.data.value;
          }
        } catch (parseError) {
          console.error('Error parsing retry response:', parseError);
          const rawResponseString = typeof generateResponse === 'string' ? generateResponse : JSON.stringify(generateResponse);
          const valueMatch = rawResponseString.match(/"value":\s*"([^"]*)"\s*}/);
          if (valueMatch && valueMatch[1]) {
            aiMessageContent = valueMatch[1];
          }
        }
        
        const aiMessage = {
          id: Date.now() + 1,
          type: "ai",
          content: aiMessageContent,
          timestamp: new Date(),
          feedback: 'na'
        };
        setIsAddingNewMessage(true);
        setMessages(prev => [...prev, aiMessage]);
      } else {
        throw new Error('No response from generateAIMessage');
      }
    } catch (retryError) {
      console.error('Retry failed:', retryError);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: "ai",
        content: "Sorry, I'm still having trouble. Please try again later.",
        timestamp: new Date(),
        feedback: 'na'
      };
      setIsAddingNewMessage(true);
      setMessages(prev => [...prev, errorMessage]);
      setShowRetryButton(true);
    } finally {
      setIsAITyping(false);
    }
  };

  const handleFileUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = false;
    input.accept = 'image/*,.pdf,.doc,.docx,.txt';
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setUploadedFiles([file]); 
      }
    };
    
    input.click();
  };

  const handleEditTitle = () => {
    setEditedTitle(chatData?.title || chatTitle || "Chat name");
    setIsEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (editedTitle.trim() && chatData?.id && !isSavingTitle) {
      setIsSavingTitle(true);
      
      try {
        // Capitalize first letter
        const capitalizedTitle = editedTitle.trim().charAt(0).toUpperCase() + editedTitle.trim().slice(1);
        
        await updateChatTitle(chatData.id, capitalizedTitle);
        
        // Update local state
        setChatData(prev => ({
          ...prev,
          title: capitalizedTitle
        }));
        
        // Show success toast
        toast.success("Chat title updated successfully!", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        
        console.log('Chat title updated successfully');
      } catch (error) {
        console.error('Error updating chat title:', error);
        
        // Show error toast
        toast.error("Failed to update chat title", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } finally {
        setIsSavingTitle(false);
        setIsEditingTitle(false);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditedTitle("");
    setIsEditingTitle(false);
  };

  const handleKeyPressTitle = (e) => {
    if (e.key === "Enter") {
      handleSaveTitle();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const handleFeedbackClick = async (messageId, feedbackType) => {
    if (!chatData?.id) {
      console.error('No chat ID available for feedback');
      return;
    }

    try {
      await updateMessageFeedback(chatData.id, messageId, feedbackType);
      
      // Update the message in the local state
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, feedback: FEEDBACK_LABELS[feedbackType] }
          : msg
      ));
      
      console.log(`Feedback updated for message ${messageId}: ${FEEDBACK_LABELS[feedbackType]}`);
    } catch (error) {
      console.error('Error updating feedback:', error);
    }
  };

  const handleCopyMessage = async (messageId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      
      // Show temporary active state for 2 seconds
      setTemporaryCopiedMessages(prev => new Set([...prev, messageId]));
      
      // Show success toast
      toast.success("Message copied to clipboard!", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Remove temporary active state after 2 seconds
      setTimeout(() => {
        setTemporaryCopiedMessages(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
      }, 2000);
      
      console.log('Message copied to clipboard');
    } catch (error) {
      console.error('Error copying message:', error);
      
      // Show error toast
      toast.error("Failed to copy message to clipboard", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  return (
    <div className="chat-bot-page">
      <Breadcrumb />
      
      <div className="chat-bot-container">
        {/* Left Section - Chat Details */}
        <div className="chat-details-section">
          <div className="chat-details-card">
            <div className="back-link">
              <button onClick={() => navigate(-1)} className="back-btn">
                ← Back
              </button>
            </div>
            
            <div className="breadcrumb-section">
              <span className="breadcrumb-text">Skills {'>'} Chats {'>'}</span>
            </div>

            <div className="chat-form">
              <div className="chat-title-container">
                {isEditingTitle ? (
                  <div className="title-edit-container">
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      onKeyDown={handleKeyPressTitle}
                      className="title-edit-input"
                      autoFocus
                    />
                    <div className="title-edit-actions">
                      <button 
                        className="chat-title-save-icon"
                        onClick={handleSaveTitle}
                        disabled={isSavingTitle}
                        title={isSavingTitle ? "Saving..." : "Save"}
                      >
                        {isSavingTitle ? (
                          <div className="loading-spinner-small"></div>
                        ) : (
                          <FiCheck size={16} />
                        )}
                      </button>
                      <button 
                        className="chat-title-cancel-icon"
                        onClick={handleCancelEdit}
                        title="Cancel"
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="title-display-container">
                    <h2 className="chat-title">{chatData?.title || chatTitle || "Chat name"}</h2>
                    <button 
                      className="chat-title-edit-icon"
                      onClick={handleEditTitle}
                      title="Edit title"
                    >
                      <FiEdit size={18} />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="description-section">
                <textarea
                  className="description-input"
                  placeholder="Please fill further information as relevant"
                  value={chatDescription}
                  onChange={(e) => setChatDescription(e.target.value)}
                  rows={8}
                />
              </div>

              <button className="add-files-btn" onClick={handleFileUpload}>
                <FiPlus size={16} />
                Add file to the chat
              </button>
              
              {uploadedFiles.length > 0 && (
                <div className="uploaded-files">
                  <h4>Uploaded File:</h4>
                  <div className="file-item">
                    <span>{uploadedFiles[0].name}</span>
                    <button 
                      onClick={() => setUploadedFiles([])}
                      className="remove-file-btn"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
              
              <p className="additional-docs-text">Any additional documents</p>
            </div>
          </div>
        </div>

        {/* Right Section - AI Chat Interface */}
        <div className="chat-interface-section">
          <div className="ai-chat-card">
            <div className="blur-element blur-purple-1"></div>
            <div className="blur-element blur-purple-2"></div>
            <div className="blur-element blur-pink"></div>
            
            <div className="ai-header">
              <div className="ai-title">
                <div className="ai-logo">
                  <div className="star-icon">★</div>
                </div>
                <span>VAR80 AI Assist</span>
              </div>
            </div>

            {isLoadingChat ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading chat messages...</p>
              </div>
            ) : !hasStartedChat ? (
              <>
                <div className="chat-icon-container">
                  <div className="chat-icon">
                    <img src={chatBotIcon} alt="Chat Bot" className="chat-bot-image" />
                  </div>
                </div>

                <div className="welcome-message">
                  <h3>Hello</h3>
                  <p>How may I help you?</p>
                </div>
              </>
            ) : (
              <div className="messages-container" ref={messagesContainerRef}>
                {hasMoreMessages && !isLoadingMore && (
                  <div className="load-more-button-container">
                    <button 
                      className="load-more-button"
                      onClick={loadMoreMessages}
                    >
                      Load Older Messages
                    </button>
                  </div>
                )}
                
                {isLoadingMore && (
                  <div className="load-more-indicator">
                    <div className="loading-spinner"></div>
                    <span>Loading older messages...</span>
                  </div>
                )}
                
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`message ${message.type === "user" ? "user-message" : "ai-message"}`}
                  >
                    {message.type === "ai" && (
                      <div className="message-avatar">
                        <div className="star-icon">★</div>
                      </div>
                    )}
                    <div className="message-content">
                      <div 
                        dangerouslySetInnerHTML={{ 
                          __html: formatMessage(message.content) 
                        }}
                      />
                      
                      {message.documentUrl && (
                        <div className="message-document">
                          <div className="document-preview">
                            <div className="document-icon">
                              <FiPaperclip size={16} />
                            </div>
                            <div className="document-info">
                              <span className="document-name">Attached Document</span>
                              <a 
                                href={message.documentUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="document-link"
                              >
                                View Document
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {message.type === "ai" && (
                        <div className="message-actions">
                          <button 
                            className={`action-icon ${message.feedback === 'like' ? 'active' : ''}`}
                            onClick={() => handleFeedbackClick(message.id, FEEDBACK_ENUM.LIKE)}
                            title="Like this response"
                          >
                            <ThumbsUp size={14} />
                          </button>
                          <button 
                            className={`action-icon ${message.feedback === 'dislike' ? 'active' : ''}`}
                            onClick={() => handleFeedbackClick(message.id, FEEDBACK_ENUM.DISLIKE)}
                            title="Dislike this response"
                          >
                            <ThumbsDown size={14} />
                          </button>
                          <button 
                            className={`action-icon ${temporaryCopiedMessages.has(message.id) ? 'active' : ''}`}
                            onClick={() => handleCopyMessage(message.id, message.content)}
                            title="Copy this response"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      )}
                      <span className="message-time">
                        {message.timestamp.toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                    </div>
                  </div>
                ))}
                
                {isAITyping && (
                  <div className="message ai-message typing-message">
                    <div className="message-avatar">
                      <div className="star-icon">★</div>
                    </div>
                    <div className="message-content">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      <span className="typing-text">typing...</span>
                    </div>
                  </div>
                )}
                
                {showRetryButton && (
                  <div className="retry-button-container">
                    <button className="retry-button" onClick={handleRetry}>
                      <span className="retry-icon">↻</span>
                      Retry AI Response
                    </button>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            )}

            <div className="input-area">
              <div className="input-container">
                <input
                  type="text"
                  placeholder="Ask me anything ..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="message-input"
                />
                <div className="input-actions">
                  <button className="action-btn" onClick={handleFileUpload}>
                    <FiImage size={16} />
                  </button>
                  <button className="action-btn">
                    <FiMic size={16} />
                  </button>
                  <button 
                    className="action-btn send-btn" 
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim()}
                  >
                    <FiSend size={16} />
                  </button>
                </div>
              </div>
              <p className="privacy-text">This conversation is only visible to you</p>
            </div>
          </div>

          <button className="ask-ai-btn">
            <MessageCircle size={20} />
            Ask AI
          </button>
        </div>
      </div>
      
      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
};

export default ChatBot;


